import { Button, useColorModeValue } from "@chakra-ui/react"
import { useOrderStripeCheckout } from "@pathwright/web/src/modules/order/OrderStripeCheckoutForm"
import useOrderPollingQuery, {
  useOnOrderFullfiled
} from "@pathwright/web/src/modules/order/useOrderPollingQuery"
import { usePathwrightContext } from "@pathwright/web/src/modules/pathwright/PathwrightContext"
import { withUserCommunity } from "@pathwright/web/src/modules/user/UserCommunityContext"
import ConnectStores from "lib/core/store/components/ConnectStores"
import flowRight from "lodash/flowRight"
import get from "lodash/get"
import merge from "lodash/merge"
import { useCallback, useEffect, useState } from "react"
import {
  REGISTER_STATE_REGISTERABLE,
  REGISTER_STATE_REGISTERED
} from "../constants"

// handle reloading various registration related data in the resource-store
// plus handle reloading the path if the path is currently cached
export const useOnRegistrationOrderFulilled = (onOrderFulfilled) => {
  const { query: contextQuery } = usePathwrightContext()

  const onRegistrationOrderFulfilled = useCallback(async (order) => {
    // refetching context mainly in order to update the user's group_role_stats
    // which controls what Home UIs are available to the user
    contextQuery.refetch()

    const cohort = App.getStore("resource").get("offering")
    if (cohort) {
      // handle registration created
      await App.getStore("resource").action._onRegistrationCreated(cohort)
        .promise
    }
    // Reload the user's registration, just so the register store has accurate
    // registration details, so we can ensure we navigate the user away from flow if necessary.
    App.getStore("register").action.loadUserRegistration({ ignoreCache: true })

    onOrderFulfilled && onOrderFulfilled(order)
  }, [])

  return onRegistrationOrderFulfilled
}

const OrderRegistrationButton = ({
  submitLabel,
  orderData,
  onOrderFulfilled,
  nextRegisterState: nextRegisterStateProp,
  children
}) => {
  const colorScheme = useColorModeValue("brand", undefined)
  const [registering, setRegistering] = useState(false)
  const [nextRegisterState, setNextRegisterState] = useState(
    nextRegisterStateProp
  )

  const {
    order: unfulfilledOrder,
    handleOrder,
    error,
    clearError
  } = useOrderStripeCheckout({
    orderType: "createRegistrationOrder",
    orderData
  })

  // expilicitly handling submitting state of SubmitButton through `registering`
  // and in that case, not using async or returning promise
  const handleSubmitOrder = () => {
    clearError()
    setRegistering(true)
    handleOrder()
  }

  const orderId = get(unfulfilledOrder, "id")
  const orderQuery = useOrderPollingQuery(orderId)
  const order = get(orderQuery, "data.order")
  const orderFulfilled = get(order, "order_status") === "fulfilled"

  // once the order is fulfilled, requery offering/s registration/s
  // then reset the registration state so that the children can be rendered
  const onRegistrationOrderFulfilled = useOnRegistrationOrderFulilled(
    (order) => {
      // HACK: struggling to get the next registration state to stick, so hardcoding it...
      setNextRegisterState(REGISTER_STATE_REGISTERED)
      // finished registering
      setRegistering(false)
      onOrderFulfilled && onOrderFulfilled(order)
    }
  )

  useOnOrderFullfiled({
    order,
    onOrderFulfilled: onRegistrationOrderFulfilled
  })

  useEffect(() => {
    setNextRegisterState(nextRegisterStateProp)
  }, [nextRegisterStateProp])

  useEffect(() => {
    if (error) setRegistering(false)
  }, [error])

  return nextRegisterState ? (
    nextRegisterState === REGISTER_STATE_REGISTERABLE ? (
      <Button
        variant="solid"
        colorScheme={colorScheme}
        onClick={handleSubmitOrder}
        isLoading={registering}
        isDisabled={registering}
      >
        {submitLabel}
      </Button>
    ) : (
      children
    )
  ) : null
}

OrderRegistrationButton.displayName = "OrderRegistrationButton"

const mapStateToProps = function (state, ownProps) {
  const offering = ownProps.offering || state.registerStore.state.offering // prettier-ignore
  const invitation = ownProps.invitation || state.registerStore.state.invitation // prettier-ignore
  const offering_option = ownProps.offering_option || state.registerStore.state.offering_option // prettier-ignore
  const registration = ownProps.registration || state.resourceStore.request.getRegistrationForOffering(offering.id) // prettier-ignore

  const useGroupSubscriptionIds = ownProps.activeCommunityGroupSubscriptions
    .filter((cgs) =>
      offering.include_with_products.includes(cgs.price.product.id)
    )
    .map((cgs) => cgs.id)

  const nextRegisterState = state.registerStore.request.getNextRegisterState({
    offering,
    registration,
    offering_option,
    includedInGroupSubscription: useGroupSubscriptionIds
  })

  const useSubscriptionPlanId =
    state.resourceStore.request.includedInUserSubscription(offering)[0] || null

  const orderData = merge(
    {
      offering_id: offering.id,
      invitation_code: invitation ? invitation.invite_token : null,
      use_subscription_plan_id: useSubscriptionPlanId,
      use_group_subscription_id: useGroupSubscriptionIds[0] || null
    },
    ownProps.orderData || {}
  )

  return {
    orderData,
    nextRegisterState,
    submitLabel: ownProps.submitLabel,
    onOrderFulfilled: ownProps.onOrderFulfilled,
    children: ownProps.children
  }
}

const stores = [
  {
    store: "register",
    watch: false
  },
  { store: "resource", watch: false }
]

export default flowRight(withUserCommunity)(
  ConnectStores(OrderRegistrationButton, stores, {}, mapStateToProps, false)
)
